import { useEffect, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Loader2, Users, Calendar, Clock, Zap, CalendarCheck, ArrowLeft, ArrowRight, CalendarX, Share2, Check, MapPin, Copy } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const STEPS = ["chi", "quando", "slot", "dettagli", "fatto"];

function fmtSlotItalian(iso) {
  const d = new Date(iso);
  const dayNames = ["Dom", "Lun", "Mar", "Mer", "Gio", "Ven", "Sab"];
  const monthNames = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
  return `${dayNames[d.getDay()]} ${d.getDate()} ${monthNames[d.getMonth()]} · ${d.getHours().toString().padStart(2, "0")}:${d.getMinutes().toString().padStart(2, "0")}`;
}

function isoToLocalDate(iso) {
  const d = new Date(iso);
  return d.toLocaleString("it-IT", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
}

function todayISO() { return new Date().toISOString().slice(0, 10); }
function daysAheadISO(n) {
  const d = new Date(); d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

export default function ZapWizard({ open, onClose, currentUser }) {
  const [step, setStep] = useState("chi");
  const [members, setMembers] = useState([]);
  const [calendarStatuses, setCalendarStatuses] = useState({});
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());

  const [startDate, setStartDate] = useState(todayISO());
  const [endDate, setEndDate] = useState(daysAheadISO(7));
  const [duration, setDuration] = useState(60);
  const [dayStart, setDayStart] = useState(10);
  const [dayEnd, setDayEnd] = useState(21);

  const [slots, setSlots] = useState([]);
  const [notConnected, setNotConnected] = useState([]);
  const [widened, setWidened] = useState(false);
  const [chosenSlot, setChosenSlot] = useState(null);

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [location, setLocation] = useState("");

  const [confirmedZap, setConfirmedZap] = useState(null);

  const reset = () => {
    setStep("chi");
    setSelected(new Set());
    setSlots([]); setChosenSlot(null);
    setTitle(""); setDescription(""); setLocation("");
    setConfirmedZap(null);
    setNotConnected([]); setWidened(false);
  };

  const close = () => { if (!loading) { reset(); onClose(); } };

  const loadMembers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/users");
      const active = res.data.filter((m) => m.status === "active");
      setMembers(active);
      // Preselect current user
      if (currentUser?.id) setSelected(new Set([currentUser.id]));
    } catch (err) {
      toast.error("Errore caricamento membri");
    } finally {
      setLoading(false);
    }
  }, [currentUser?.id]);

  useEffect(() => { if (open) { reset(); loadMembers(); } }, [open, loadMembers]);

  const toggleMember = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id); else s.add(id);
    setSelected(s);
  };

  const goFind = async () => {
    if (selected.size < 2) return toast.error("Seleziona almeno 2 persone");
    setLoading(true);
    try {
      const res = await api.post("/zap/find-slots", {
        user_ids: Array.from(selected),
        start_date: startDate,
        end_date: endDate,
        duration_minutes: duration,
        day_start_hour: dayStart,
        day_end_hour: dayEnd,
      });
      setSlots(res.data.slots);
      setNotConnected(res.data.not_connected_members || []);
      setWidened(res.data.widened);
      setStep("slot");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Nessuno slot trovato");
    } finally {
      setLoading(false);
    }
  };

  const goConfirm = async () => {
    if (!title.trim()) return toast.error("Metti un titolo (es. Pranzo da nonna)");
    setLoading(true);
    try {
      const res = await api.post("/zap/confirm", {
        user_ids: Array.from(selected),
        title: title.trim(),
        description: description.trim(),
        location: location.trim(),
        start_iso: chosenSlot.start,
        end_iso: chosenSlot.end,
        creator_user_id: currentUser.id,
      });
      setConfirmedZap({ ...res.data, title, location, start_iso: chosenSlot.start });
      setStep("fatto");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore creazione evento");
    } finally {
      setLoading(false);
    }
  };

  const shareUrl = confirmedZap ? `${window.location.origin}/event/${confirmedZap.public_id}` : "";
  const shareMessage = confirmedZap
    ? `⚡ Facciamo uno ZAP!\n\n${confirmedZap.title}${confirmedZap.location ? `\n📍 ${confirmedZap.location}` : ""}\n📅 ${isoToLocalDate(confirmedZap.start_iso)}\n\nDettagli: ${shareUrl}`
    : "";
  const qrUrl = confirmedZap
    ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(shareUrl)}`
    : "";

  const shareWhatsApp = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: confirmedZap.title, text: shareMessage }); return; }
      catch { /* fall through */ }
    }
    window.open(`https://wa.me/?text=${encodeURIComponent(shareMessage)}`, "_blank");
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && close()}>
      <DialogContent data-testid="zap-wizard" className="max-w-lg rounded-3xl border-[#E8E1D9] bg-white max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="mb-2 inline-flex w-fit items-center gap-2 rounded-full papaya-gradient px-3 py-1">
            <Zap className="h-3 w-3 text-white" fill="white" />
            <span className="text-[10px] font-bold uppercase tracking-[0.25em] text-white">Facciamo uno ZAP</span>
          </div>
          <DialogTitle className="font-display text-2xl font-bold text-[#2D2926]">
            {step === "chi" && "Con chi ci vediamo?"}
            {step === "quando" && "Quando?"}
            {step === "slot" && "Quando siete tutti liberi ✨"}
            {step === "dettagli" && "Dettagli dell'incontro"}
            {step === "fatto" && "ZAP creato! ⚡"}
          </DialogTitle>
        </DialogHeader>

        {/* Progress dots */}
        {step !== "fatto" && (
          <div className="mb-2 flex justify-center gap-1.5">
            {STEPS.slice(0, 4).map((s) => (
              <span key={s} className={`h-1.5 rounded-full transition-all ${
                s === step ? "w-8 bg-[#FF7C43]" : "w-1.5 bg-[#E8E1D9]"
              }`} />
            ))}
          </div>
        )}

        {/* STEP 1: CHI */}
        {step === "chi" && (
          <div className="space-y-3">
            <p className="text-sm text-[#5C5855]">Chi vuoi coinvolgere in questo ZAP?</p>
            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#FF7C43]" /></div>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {members.map((m) => {
                  const isChecked = selected.has(m.id);
                  return (
                    <button
                      key={m.id}
                      data-testid={`select-member-${m.email}`}
                      onClick={() => toggleMember(m.id)}
                      className={`flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition ${
                        isChecked ? "border-[#FF7C43] bg-[#FF7C43]/5" : "border-[#E8E1D9] bg-white hover:border-[#C87941]"
                      }`}
                    >
                      <div className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border-2 ${
                        isChecked ? "border-[#FF7C43] bg-[#FF7C43]" : "border-[#E8E1D9]"
                      }`}>
                        {isChecked && <Check className="h-3 w-3 text-white" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-[#2D2926]">
                          {m.full_name} {m.id === currentUser?.id && <span className="text-xs text-[#C87941]">(tu)</span>}
                        </p>
                        <p className="text-xs text-[#5C5855] truncate">{m.email}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={close} className="flex-1 rounded-full border-[#E8E1D9]">Annulla</Button>
              <Button
                data-testid="step-chi-next"
                onClick={() => setStep("quando")}
                disabled={selected.size < 2}
                className="flex-1 rounded-full papaya-gradient text-white"
              >
                Avanti <ArrowRight className="ml-1 h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* STEP 2: QUANDO */}
        {step === "quando" && (
          <div className="space-y-3">
            <p className="text-sm text-[#5C5855]">In che giorni cerchiamo? (max 45 giorni)</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">Da</label>
                <Input data-testid="zap-start-date" type="date" value={startDate} min={todayISO()} onChange={(e) => setStartDate(e.target.value)} className="rounded-xl border-[#E8E1D9]" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">A</label>
                <Input data-testid="zap-end-date" type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} className="rounded-xl border-[#E8E1D9]" />
              </div>
            </div>

            <div className="pt-2">
              <label className="mb-1 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">Quanto dura?</label>
              <div className="flex gap-2 flex-wrap">
                {[60, 90, 120, 180, 240].map((m) => (
                  <button key={m} onClick={() => setDuration(m)}
                    data-testid={`duration-${m}`}
                    className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                      duration === m ? "border-[#FF7C43] bg-[#FF7C43] text-white" : "border-[#E8E1D9] bg-white text-[#5C5855]"
                    }`}>
                    {m < 60 ? `${m}min` : m === 60 ? "1h" : `${m/60}h`}
                  </button>
                ))}
              </div>
            </div>

            <div className="pt-2">
              <label className="mb-1 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">Orario giornata</label>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-[#5C5855]">dalle</span>
                <select value={dayStart} onChange={(e) => setDayStart(+e.target.value)} className="rounded-lg border border-[#E8E1D9] px-2 py-1">
                  {Array.from({length: 24}, (_, i) => i).map(h => <option key={h} value={h}>{h}:00</option>)}
                </select>
                <span className="text-[#5C5855]">alle</span>
                <select value={dayEnd} onChange={(e) => setDayEnd(+e.target.value)} className="rounded-lg border border-[#E8E1D9] px-2 py-1">
                  {Array.from({length: 24}, (_, i) => i+1).map(h => <option key={h} value={h}>{h}:00</option>)}
                </select>
              </div>
            </div>

            <div className="flex gap-2 pt-3">
              <Button variant="outline" onClick={() => setStep("chi")} className="rounded-full border-[#E8E1D9]">
                <ArrowLeft className="mr-1 h-4 w-4" /> Indietro
              </Button>
              <Button
                data-testid="step-quando-find"
                onClick={goFind}
                disabled={loading}
                className="flex-1 rounded-full papaya-gradient text-white"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                  <><Zap className="mr-1 h-4 w-4" fill="white" /> Trova quando siamo liberi</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3: SLOT RESULTS */}
        {step === "slot" && (
          <div className="space-y-3">
            {notConnected.length > 0 && (
              <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900">
                <p className="font-semibold flex items-center gap-1"><CalendarX className="h-3 w-3" /> Non hanno collegato Google Calendar:</p>
                <p className="mt-1">{notConnected.map(m => m.full_name).join(", ")}</p>
                <p className="mt-1 opacity-80">Li invitiamo comunque — riceveranno il link e risponderanno manualmente.</p>
              </div>
            )}
            {widened && (
              <div className="rounded-2xl bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900">
                🔍 Ho allargato la fascia oraria (7-23) per trovare più opzioni.
              </div>
            )}
            {slots.length === 0 ? (
              <div className="text-center py-6">
                <CalendarX className="mx-auto h-12 w-12 text-[#C87941] mb-2" />
                <p className="font-semibold text-[#2D2926]">Nessuno slot libero trovato 😅</p>
                <p className="text-sm text-[#5C5855] mt-1">Prova ad allargare le date o ridurre la durata.</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-[#5C5855]">Ecco i momenti liberi per tutti. Scegli uno:</p>
                <div className="space-y-2 max-h-72 overflow-y-auto">
                  {slots.map((s, i) => (
                    <button
                      key={s.start}
                      data-testid={`slot-${i}`}
                      onClick={() => { setChosenSlot(s); setStep("dettagli"); }}
                      className="flex w-full items-center gap-3 rounded-2xl border border-[#E8E1D9] bg-white p-3 text-left transition hover:-translate-y-0.5 hover:border-[#FF7C43] hover:shadow-md"
                    >
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FF7C43]/10 text-[#FF7C43]">
                        <Calendar className="h-5 w-5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-[#2D2926] text-sm">{fmtSlotItalian(s.start)}</p>
                        <p className="text-xs text-[#5C5855]">
                          fino alle {new Date(s.end).toLocaleTimeString("it-IT", {hour: "2-digit", minute: "2-digit"})}
                        </p>
                      </div>
                      <ArrowRight className="h-4 w-4 text-[#C87941]" />
                    </button>
                  ))}
                </div>
              </>
            )}
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep("quando")} className="rounded-full border-[#E8E1D9]">
                <ArrowLeft className="mr-1 h-4 w-4" /> Cambia date
              </Button>
            </div>
          </div>
        )}

        {/* STEP 4: DETTAGLI */}
        {step === "dettagli" && chosenSlot && (
          <div className="space-y-3">
            <div className="rounded-2xl bg-[#FBF9F6] border border-[#E8E1D9] p-3">
              <p className="text-xs font-bold uppercase tracking-wider text-[#C87941]">📅 Quando</p>
              <p className="font-semibold text-[#2D2926] mt-1 text-sm">{isoToLocalDate(chosenSlot.start)}</p>
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">Titolo *</label>
              <Input data-testid="zap-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Es. Pranzo da nonna" className="rounded-xl border-[#E8E1D9]" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">Dove (opzionale)</label>
              <Input data-testid="zap-location" value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Es. Casa nonna, Via Roma 12" className="rounded-xl border-[#E8E1D9]" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-bold uppercase tracking-[0.2em] text-[#C87941]">Nota (opzionale)</label>
              <Textarea data-testid="zap-description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Es. Porto il dolce, chi porta l'acqua?" className="rounded-xl border-[#E8E1D9]" rows={2} />
            </div>
            <div className="flex gap-2 pt-2">
              <Button variant="outline" onClick={() => setStep("slot")} className="rounded-full border-[#E8E1D9]">
                <ArrowLeft className="mr-1 h-4 w-4" /> Indietro
              </Button>
              <Button
                data-testid="zap-confirm"
                onClick={goConfirm}
                disabled={loading || !title.trim()}
                className="flex-1 rounded-full papaya-gradient text-white"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                  <><Zap className="mr-1 h-4 w-4" fill="white" /> Crea ZAP</>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* STEP 5: FATTO! */}
        {step === "fatto" && confirmedZap && (
          <div className="space-y-4">
            <div className="rounded-2xl bg-green-50 border border-green-200 p-4 text-center">
              <CalendarCheck className="mx-auto h-10 w-10 text-green-600 mb-2" />
              <p className="font-semibold text-[#2D2926]">Evento creato su Google Calendar!</p>
              <p className="text-xs text-[#5C5855] mt-1">Ognuno riceverà l'invito nella sua Gmail.</p>
              {confirmedZap.google_html_link && (
                <a href={confirmedZap.google_html_link} target="_blank" rel="noreferrer" className="text-xs font-semibold text-[#FF7C43] hover:underline mt-1 inline-block">
                  Apri su Google Calendar →
                </a>
              )}
            </div>

            <div className="text-center">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#C87941] mb-2">Condividi con la famiglia</p>
              <img src={qrUrl} alt="QR code" className="mx-auto rounded-2xl border border-[#E8E1D9]" width="180" height="180" />
              <p className="mt-2 text-[11px] text-[#5C5855]">Zia inquadra col telefono e entra 📱</p>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                data-testid="zap-share-whatsapp"
                onClick={shareWhatsApp}
                className="w-full rounded-full bg-[#25D366] text-white hover:bg-[#22c55e]"
              >
                <Share2 className="mr-2 h-4 w-4" /> Manda su WhatsApp
              </Button>
              <Button
                variant="outline"
                onClick={() => { navigator.clipboard.writeText(shareUrl); toast.success("Link copiato"); }}
                className="w-full rounded-full border-[#E8E1D9]"
              >
                <Copy className="mr-2 h-4 w-4" /> Copia link
              </Button>
              <Button variant="outline" onClick={close} className="w-full rounded-full border-[#E8E1D9]">Chiudi</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
